import React from 'react';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { t } from 'i18next';
import { Checkbox, TextField, Typography } from 'drift-ui';
import styles from '../styles/FactoringFundsForm.scss';
import commonStyles from '../styles/common.scss';
import { formatPhone } from '../utils/utils';

export interface FactoringFundsFormData {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  companyName?: string;
  email?: string;
  agreementOfPurchase?: string;
  agreementOFactoringDeduction?: string;
}

interface FactoringFundsFormProps {
  cost: string;
  isMonthly: boolean;
  control: Control<FactoringFundsFormData>;
  errors: FieldErrors<FactoringFundsFormData>;
}

const FactoringFundsForm: React.FC<FactoringFundsFormProps> = ({ cost, isMonthly, control, errors }) => {
  const purchaseAcceptance = t('I certify I possess the authority to purchase on behalf of the company.');
  const costAcceptance = t('I certify that you can begin deducting ${{cost}}/{{term}} from my factoring account.', {
    cost,
    term: isMonthly ? 'month' : 'year',
  });

  return (
    <>
      <div className={commonStyles.subHeader}>{t('Confirm account information')}</div>
      <div className={commonStyles.row}>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('First Name')}
              placeholder={t('First Name')}
              type="text"
              helperText={errors?.firstName?.message}
              error={!!errors?.firstName}
            />
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Last Name')}
              placeholder={t('Last Name')}
              type="text"
              helperText={errors?.lastName?.message}
              error={!!errors?.lastName}
            />
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              className={commonStyles.leftRowItem}
              label={t('Phone Number')}
              placeholder={t('Phone Number')}
              type="text"
              onChange={(e) => field.onChange(formatPhone(e.target.value, true))}
              helperText={errors?.phoneNumber?.message}
              error={!!errors?.phoneNumber}
            />
          )}
        />
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Company Name')}
              placeholder={t('Company Name')}
              type="text"
              disabled
              helperText={errors?.companyName?.message}
              error={!!errors?.companyName}
            />
          )}
        />
      </div>
      <div className={commonStyles.row}>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              required
              label={t('Email')}
              placeholder={t('Email')}
              type="text"
              disabled
              helperText={errors?.email?.message}
              error={!!errors?.email}
            />
          )}
        />
      </div>
      <div className={styles.checkboxRow}>
        <Controller
          name="agreementOfPurchase"
          control={control}
          render={({ field }) => (
            <Checkbox {...field} onChange={(e) => field.onChange(e.target.checked ? purchaseAcceptance : '')} />
          )}
        />
        <div className={commonStyles.certifyText}>{purchaseAcceptance}</div>
      </div>
      <div className={styles.lastRow}>
        <Controller
          name="agreementOFactoringDeduction"
          control={control}
          render={({ field }) => (
            <Checkbox {...field} onChange={(e) => field.onChange(e.target.checked ? costAcceptance : '')} />
          )}
        />
        <div className={commonStyles.certifyText}>{costAcceptance}</div>
      </div>
      <Typography color="error">
        {errors?.agreementOfPurchase?.message || errors?.agreementOFactoringDeduction?.message}
      </Typography>
    </>
  );
};

export default FactoringFundsForm;
